import { FadeIn, MaskedLine } from "@/components/shared/Reveal";
import CTASection from "@/components/shared/CTASection";
import { BadgeCheck, Zap, Workflow, LayoutDashboard, Plug2, Gauge, Sparkles } from "lucide-react";

const capabilities = [
  {
    icon: Zap,
    title: "App Engine",
    body: "Custom enterprise applications on the Now Platform — scoped apps, data models, and business rules built to fit your processes, not the other way around.",
  },
  {
    icon: Workflow,
    title: "Workflow & Automation",
    body: "Flow Designer and Workflow Design Studio expertise — automating the approvals, escalations, and orchestrations that keep the enterprise moving.",
  },
  {
    icon: LayoutDashboard,
    title: "Workspaces, UI Builder & Dashboards",
    body: "Configurable workspaces and purpose-built experiences in UI Builder, with dashboards that turn platform data into decisions.",
  },
  {
    icon: Plug2,
    title: "REST APIs & Integrations",
    body: "ServiceNow does not live alone. We connect it cleanly to your legacy systems, cloud services, and data — integration as architecture, not afterthought.",
  },
  {
    icon: Gauge,
    title: "Rapid Prototyping",
    body: "Working prototypes in days, tested by the people who will use them. Decisions made on evidence, not slideware.",
  },
  {
    icon: Sparkles,
    title: "AI-Assisted Development",
    body: "AI Agents and Build Agent as daily practice — accelerating delivery, exploring designs, and compressing the path from idea to application.",
  },
];

const ServiceNow = () => (
  <main data-testid="servicenow-page" className="bg-ink">
    <section className="relative pt-40 pb-24 md:pb-32 overflow-hidden">
      <div className="absolute -top-32 left-1/3 w-[560px] h-[560px] rounded-full bg-amber-600/10 blur-3xl pointer-events-none" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
        <MaskedLine delay={0.1}>
          <p className="label-mono">ServiceNow Expertise</p>
        </MaskedLine>
        <h1 className="mt-8 font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-slate-100">
          <MaskedLine delay={0.25}>Enterprise applications,</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="italic text-gold">modernized on the platform.</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.6}>
          <p className="mt-10 max-w-2xl text-lg md:text-xl text-slate-400 font-light leading-relaxed">
            More than a decade at ServiceNow as Solution Architect and Platform Developer —
            building the applications, workflows, and integrations that run the modern enterprise.
          </p>
        </MaskedLine>
      </div>
    </section>

    <section className="border-t hairline bg-ink-light">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-16 flex flex-col md:flex-row gap-8 md:gap-16">
        {[
          ["Certified System Administrator", "CSA"],
          ["Certified Application Developer", "CAD"],
        ].map(([label, abbr], i) => (
          <FadeIn key={abbr} delay={0.1 * i} className="flex items-center gap-5">
            <BadgeCheck className="text-gold shrink-0" size={36} strokeWidth={1.5} />
            <div>
              <p className="font-mono-tech text-amber-500/70 text-xs tracking-[0.3em]">{abbr}</p>
              <p className="font-serif text-xl md:text-2xl text-slate-100">ServiceNow {label}</p>
            </div>
          </FadeIn>
        ))}
      </div>
    </section>

    <section className="border-t hairline bg-ink">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-36">
        <FadeIn>
          <p className="label-mono mb-4">The Craft</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-slate-100 max-w-2xl leading-tight">
            Ten years of building on the Now Platform.
          </h2>
        </FadeIn>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {capabilities.map((c, i) => (
            <FadeIn
              key={c.title}
              delay={0.08 * i}
              className="border hairline bg-ink-light p-8 md:p-10 transition-[border-color,transform,background-color] duration-500 hover:border-amber-600/50 hover:-translate-y-1 hover:bg-slate-900/60"
            >
              <c.icon className="text-gold mb-6" size={26} strokeWidth={1.5} />
              <h3 className="font-serif text-2xl text-slate-100 mb-4">{c.title}</h3>
              <p className="text-slate-400 font-light leading-relaxed text-sm">{c.body}</p>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    <section className="relative border-t hairline bg-ink-light overflow-hidden">
      <div className="absolute -bottom-32 right-0 w-[480px] h-[480px] rounded-full bg-brass/10 blur-3xl pointer-events-none" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-36 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <FadeIn className="lg:col-span-5">
          <p className="label-mono mb-4">A Principle, Not a Pitch</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-slate-100 leading-tight">
            Not every application belongs on ServiceNow.
          </h2>
        </FadeIn>
        <FadeIn delay={0.15} className="lg:col-span-6 lg:col-start-7 space-y-6">
          <p className="text-slate-400 font-light leading-relaxed text-lg">
            We are fluent in ServiceNow — and that fluency is exactly why we will tell you when
            it is the wrong answer. Some applications belong on a custom stack, some in a SaaS
            product, and some should simply be retired.
          </p>
          <p className="text-slate-400 font-light leading-relaxed text-lg">
            Nexus IPG is a modernization consultancy with deep ServiceNow mastery, not a
            ServiceNow-only shop. When the platform fits, few can build on it better. When it
            does not, we will say so — and show you what does.
          </p>
          <p className="font-serif text-2xl text-amber-100/90 italic border-l-2 border-amber-500/70 pl-6 leading-snug">
            "The right solution is the one that serves your people. The platform is a means,
            never the mandate."
          </p>
        </FadeIn>
      </div>
    </section>

    <CTASection
      title="Wondering if ServiceNow fits?"
      sub="Ask us. If the honest answer is no, you will hear it — along with what we would do instead."
    />
  </main>
);

export default ServiceNow;
